/**
 * Bottom Sheet Controller
 * Manages slide-up modals for mobile filter/sort controls.
 * Supports backdrop tap-to-dismiss, swipe-to-dismiss, and programmatic open/close.
 */

export function createBottomSheet({ document, window, onClose = null }) {
  let backdrop = null;
  let sheet = null;
  let isOpen = false;
  let touchStartY = 0;
  let touchCurrentY = 0;
  let isDragging = false;

  function init(sheetId, backdropId) {
    backdrop = document.getElementById(backdropId);
    sheet = document.getElementById(sheetId);

    if (!backdrop || !sheet) {
      console.warn("[bottomSheet] Elements not found:", sheetId, backdropId);
      return false;
    }

    // Backdrop click to close
    backdrop.addEventListener("click", close);

    // Touch events for swipe-to-dismiss
    sheet.addEventListener("touchstart", handleTouchStart, { passive: true });
    sheet.addEventListener("touchmove", handleTouchMove, { passive: false });
    sheet.addEventListener("touchend", handleTouchEnd, { passive: true });

    // Keyboard escape to close
    document.addEventListener("keydown", handleKeyDown);

    return true;
  }

  function handleTouchStart(e) {
    if (!isOpen) return;
    const touch = e.touches[0];
    touchStartY = touch.clientY;
    touchCurrentY = touch.clientY;
    isDragging = false;
  }

  function handleTouchMove(e) {
    if (!isOpen) return;
    const touch = e.touches[0];
    touchCurrentY = touch.clientY;
    const deltaY = touchCurrentY - touchStartY;

    // Only allow dragging downward
    if (deltaY > 10) {
      isDragging = true;
      // Prevent scroll when dragging sheet
      e.preventDefault();
      // Apply transform for visual feedback
      sheet.style.transform = `translateY(${Math.max(0, deltaY)}px)`;
      sheet.style.transition = "none";
    }
  }

  function handleTouchEnd() {
    if (!isOpen || !isDragging) {
      resetTransform();
      return;
    }

    const deltaY = touchCurrentY - touchStartY;
    const sheetHeight = sheet.offsetHeight;

    // Close if dragged more than 30% of sheet height
    if (deltaY > sheetHeight * 0.3) {
      close();
    } else {
      resetTransform();
    }

    isDragging = false;
  }

  function resetTransform() {
    if (!sheet) return;
    sheet.style.transform = "";
    sheet.style.transition = "";
  }

  function handleKeyDown(e) {
    if (e.key === "Escape" && isOpen) {
      e.preventDefault();
      close();
    }
  }

  function open() {
    if (!backdrop || !sheet || isOpen) return;

    isOpen = true;
    resetTransform();

    // Prevent body scroll
    document.body.style.overflow = "hidden";

    // Trigger animations
    requestAnimationFrame(() => {
      backdrop.classList.add("open");
      sheet.classList.add("open");
    });
  }

  function close() {
    if (!backdrop || !sheet || !isOpen) return;

    isOpen = false;
    resetTransform();

    // Allow body scroll
    document.body.style.overflow = "";

    // Trigger close animations
    backdrop.classList.remove("open");
    sheet.classList.remove("open");

    if (typeof onClose === "function") {
      onClose();
    }
  }

  function toggle() {
    if (isOpen) {
      close();
    } else {
      open();
    }
  }

  function destroy() {
    if (backdrop) {
      backdrop.removeEventListener("click", close);
    }
    if (sheet) {
      sheet.removeEventListener("touchstart", handleTouchStart);
      sheet.removeEventListener("touchmove", handleTouchMove);
      sheet.removeEventListener("touchend", handleTouchEnd);
    }
    document.removeEventListener("keydown", handleKeyDown);

    backdrop = null;
    sheet = null;
    isOpen = false;
  }

  return {
    init,
    open,
    close,
    toggle,
    destroy,
    isOpen: () => isOpen,
  };
}
